import { type ReactElement } from 'react';
import cx from 'clsx';
import { Menu, type MenuProps } from 'react-aria-components';
import { Popover, type PopoverProps } from '#components/popover/popover';
import styles from './menu-content.module.css';

interface MenuContentProps<T> extends MenuProps<T> {
  afterContent?: React.ReactNode;
  beforeContent?: React.ReactNode;
  placement?: PopoverProps['placement'];
  afterContentClassName?: string;
  beforeContentClassName?: string;
  popoverClassName?: string;
  portalContainerId?: string;
  crossOffset?: PopoverProps['crossOffset'];
  isNonModal?: PopoverProps['isNonModal'];
}

export function MenuContent<T extends object>({
  className,
  afterContent,
  beforeContent,
  afterContentClassName,
  beforeContentClassName,
  popoverClassName,
  portalContainerId,
  placement = 'bottom start',
  crossOffset = 0,
  isNonModal,
  ...props
}: MenuContentProps<T>): ReactElement {
  // react-aria modal escapes the test container, we render it where it can find the modal (i.e. <div> component with 'ember-testing' id)
  const TEST_PORTAL_CONTAINER =
    ((portalContainerId ? document.getElementById(portalContainerId) : undefined) ||
      document.getElementById('ember-testing')) ??
    undefined;

  // react-aria's `Menu` renders its own `Dialog`, which gives the surrounding `Popover`
  // `role="dialog"` plus an auto-generated `aria-labelledby` that points at nothing. Forward
  // the menu's own label to the popover so the dialog gets a real accessible name.
  return (
    <Popover
      UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
      aria-label={props['aria-label']}
      aria-labelledby={props['aria-labelledby']}
      className={cx(styles.popover, popoverClassName)}
      crossOffset={crossOffset}
      isNonModal={isNonModal}
      placement={placement}
    >
      {beforeContent ? (
        <div className={cx(styles.beforeContent, beforeContentClassName)}>{beforeContent}</div>
      ) : null}
      <Menu className={cx(styles.menu, className)} {...props} />
      {afterContent ? (
        <div className={cx(styles.afterContent, afterContentClassName)}>{afterContent}</div>
      ) : null}
    </Popover>
  );
}
